/**
 * Profile pictures for the mock inbox (no network, no real people): small abstract SVG "photos" standing in for
 * the Messenger / Instagram / LINE profile pictures that api/app/profiles.py loads in production.
 * Some chats keep `null` (the customer has no picture → initial circle) and one link is broken on purpose
 * (an expired Meta CDN link → initial circle + refreshProfile()).
 */

function svg(body: string): string {
  return (
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">${body}</svg>`)
  )
}

const SUNSET = svg(
  '<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fbbf24"/><stop offset="1" stop-color="#ef4444"/></linearGradient></defs>' +
    '<rect width="96" height="96" fill="url(#g)"/><circle cx="48" cy="58" r="20" fill="#fde68a"/>' +
    '<path d="M0 70 Q24 56 48 68 T96 64 V96 H0Z" fill="#7c2d12"/>',
)
const LEAF = svg(
  '<rect width="96" height="96" fill="#dcfce7"/>' +
    '<ellipse cx="40" cy="44" rx="26" ry="13" transform="rotate(-35 40 44)" fill="#22c55e"/>' +
    '<ellipse cx="60" cy="58" rx="24" ry="11" transform="rotate(25 60 58)" fill="#15803d"/>' +
    '<path d="M22 74 L70 30" stroke="#14532d" stroke-width="2"/>',
)
const SKY = svg(
  '<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#bae6fd"/><stop offset="1" stop-color="#3b82f6"/></linearGradient></defs>' +
    '<rect width="96" height="96" fill="url(#g)"/><circle cx="34" cy="50" r="13" fill="#fff"/><circle cx="50" cy="44" r="17" fill="#fff"/>' +
    '<circle cx="66" cy="52" r="12" fill="#fff"/><rect x="28" y="50" width="46" height="14" rx="7" fill="#fff"/>',
)
const BOKEH = svg(
  '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fbcfe8"/><stop offset="1" stop-color="#9333ea"/></linearGradient></defs>' +
    '<rect width="96" height="96" fill="url(#g)"/><circle cx="26" cy="30" r="12" fill="#fff" opacity=".45"/>' +
    '<circle cx="64" cy="40" r="18" fill="#fff" opacity=".3"/><circle cx="44" cy="70" r="10" fill="#fff" opacity=".5"/>',
)
const OCEAN = svg(
  '<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ccfbf1"/><stop offset="1" stop-color="#0e7490"/></linearGradient></defs>' +
    '<rect width="96" height="96" fill="url(#g)"/><path d="M0 52 Q16 44 32 52 T64 52 T96 52 V96 H0Z" fill="#0891b2"/>' +
    '<path d="M0 66 Q16 58 32 66 T64 66 T96 66 V96 H0Z" fill="#155e75"/>',
)
const BOWL = svg(
  '<rect width="96" height="96" fill="#fef3c7"/><circle cx="36" cy="46" r="9" fill="#65a30d"/><circle cx="52" cy="42" r="10" fill="#84cc16"/>' +
    '<circle cx="62" cy="50" r="8" fill="#f97316"/><path d="M18 52 H78 A30 26 0 0 1 18 52Z" fill="#b45309"/>',
)

/**
 * Stands in for an expired Meta CDN link: the <img> fails → the initial circle shows and the UI asks for a fresh
 * profile. A truncated PNG (not a dead https host) so headless screen tests see no network error in the console.
 */
export const MOCK_BROKEN_AVATAR = 'data:image/png;base64,iVBORw0KGgo='

/** conversation id → contact picture (missing = no picture). */
export const MOCK_AVATARS: Record<string, string> = {
  c101: SUNSET,
  c102: LEAF,
  c103: BOKEH,
  c105: SKY,
  c106: OCEAN,
  c201: BOWL,
  c202: SKY,
  c203: MOCK_BROKEN_AVATAR,
  c204: SUNSET,
  c206: BOKEH,
  c208: OCEAN,
  c209: LEAF,
  c211: BOWL,
  c302: BOKEH,
  c304: LEAF,
  c305: SUNSET,
  c402: OCEAN,
  c404: SKY,
  c405: BOWL,
}
